import type { TipArt } from './SetupJourneyArt';
import { hostHas, type HostFeature } from '../shared/host-capabilities';
import { hostWords } from '../shared/host-words';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md

/**
 * TIPS WHILE A NEW JOB SETS UP (owner-approved October 3, 2026).
 *
 * Setup takes minutes; the bottom of its screen uses them. About half the tips say how
 * to use Paige and what to expect, half brag about what sets it apart and what is
 * easy to miss, all in a positive voice and every one TRUE of the product today (checked
 * against the code and docs on October 3, 2026). No tip promises a number, and none says
 * the first documents wait "to be taught": they wait only when the job is not sure yet
 * (ReviewState). Line arithmetic is per row (quantity × unit price = amount), never a
 * claim that rows add up to the total. Typo tolerance in lists is opt-in. Waiting for the
 * operator is a choice they make (Manual), never something it always does; an export
 * schedule is a time each day (owner, October 4, 2026).
 *
 * Each tip's one "Learn more" opens its longer description from the bottom of the screen
 * (owner, October 3, 2026: it was a "Check out …" button per tip). Its X closes it (no Got
 * it, owner, October 4, 2026), and it never links out: the screens a link would open (Speed
 * boost, Export, Review thresholds…) can change the very job setup is still building.
 */
export type SetupTip = {
  id: string;
  kind: 'use' | 'feature';
  headline: string;
  line: string;
  description: string;
  art: TipArt;
  /** Names shown as chips under the description (where a list says it best). */
  chips?: string[];
  /** Shown only on a computer that can do it. */
  needs?: 'ai-review';
  /** PAIGE: shown only where the host has this (a tip about the operator's own computer, say). */
  host?: HostFeature;
};

export const SETUP_TIPS: readonly SetupTip[] = [
  {
    id: 'no-templates',
    kind: 'feature',
    art: 'plain',
    headline: 'No templates. No zones.',
    line: 'It learns your documents by itself.',
    description:
      'There’s nothing to draw or set up. Cortexa learns your kinds of documents and their fields from the documents themselves.',
  },
  {
    id: 'asks-more',
    kind: 'use',
    art: 'review',
    headline: 'New jobs ask more at first',
    line: 'Each document you confirm makes it surer.',
    description:
      'A new job sends on only what it’s sure of; the rest waits for a quick look. Every document you confirm makes it surer, so fewer need a look over time.',
  },
  {
    id: 'private',
    host: 'local-performance',
    kind: 'feature',
    art: 'private',
    headline: 'Your documents can stay with you',
    line: 'It can read on your computer. No cloud needed.',
    description:
      'Paige can read your documents right here on this computer with its own AI, so they don’t have to leave your network.',
  },
  {
    id: 'clears-many',
    host: 'confirm-rejudge',
    kind: 'use',
    art: 'clear',
    headline: 'One confirm can clear many',
    line: 'Confirming takes another look at what’s waiting.',
    description:
      'When you confirm a document, Cortexa takes another look at the ones still waiting, and any that now pass go through on their own.',
  },
  {
    id: 'thresholds',
    kind: 'feature',
    art: 'gauge',
    headline: 'You decide how sure is sure',
    line: 'Set a review threshold for each field.',
    description:
      'Every field can have its own review threshold, so you decide how sure Cortexa must be before something goes through without a look.',
  },
  {
    id: 'click-field',
    kind: 'use',
    art: 'plain',
    headline: 'Click a field, see it on the page',
    line: 'Its value lights up where it was read.',
    description:
      'Click a field and the page jumps to where its value came from and lights it up, so checking is quick.',
  },
  {
    id: 'math',
    kind: 'feature',
    art: 'math',
    headline: 'It checks the math',
    line: 'Each line: quantity × price = amount.',
    description:
      'On every line item, Paige checks that quantity times price equals the amount. A line that doesn’t add up waits for you, with the fix one click away.',
  },
  {
    id: 'right-click',
    kind: 'use',
    art: 'assign',
    headline: 'Right-click to fill a field',
    line: 'Right-click printed text, then Assign to field.',
    description: 'Right-click any printed text on the page and assign it to a field. No retyping.',
  },
  {
    id: 'lookups',
    kind: 'feature',
    art: 'lookup',
    headline: 'Fill fields from your lists',
    line: 'Match a vendor, fill the rest from its row.',
    description:
      'Add a list, like your vendors. Paige matches what it reads to your list even when case, punctuation, spacing or Inc and LLC differ, uses your list’s spelling, and can fill other fields from the same row.',
  },
  {
    id: 'new-layout',
    kind: 'use',
    art: 'new',
    headline: 'Something new? It asks first',
    line: 'A new layout or kind of document waits for you.',
    description:
      'When a new layout or kind of document shows up, it waits for you instead of being guessed. Confirm a few and the job learns it, with nothing to set up.',
  },
  {
    id: 'speed-boost',
    host: 'local-performance',
    kind: 'feature',
    art: 'speed',
    headline: 'Got a graphics card?',
    line: 'Speed boost turns on by itself.',
    description:
      'With a high-end graphics card, Paige reads pages faster. Speed boost turns on by itself when your computer has one.',
  },
  {
    id: 'separators',
    kind: 'use',
    art: 'separator',
    headline: 'What’s a break sheet?',
    line: 'A page you put between documents.',
    description:
      'A break sheet is a page you put between documents before you scan. Paige sees it and starts a new document right there.',
  },
  {
    id: 'naming',
    kind: 'feature',
    art: 'folders',
    headline: 'Folders and file names, built for you',
    line: 'From what’s on each document.',
    description:
      'Paige can name your folders and files from what’s on each document, like the vendor and the date, and it never lets two files overwrite each other.',
  },
  {
    id: 'print-separators',
    kind: 'use',
    art: 'separator',
    headline: 'Keep break sheets by the scanner',
    line: 'Print a reusable stack once.',
    description:
      'Print a stack of break sheets once and keep them by the scanner. They’re reusable.',
  },
  {
    id: 'export',
    host: 'desktop-export',
    kind: 'feature',
    art: 'export',
    headline: 'Send it anywhere',
    line: 'SharePoint, Google Drive, Dropbox and more.',
    description:
      'Send finished documents to a folder or Content Central, and a copy to any of these too.',
    chips: [
      'A folder',
      'Content Central',
      'SharePoint',
      'OneDrive',
      'Google Drive',
      'Dropbox',
      'Nucleus One',
    ],
  },
  {
    id: 'fix-teaches',
    kind: 'use',
    art: 'learn',
    headline: 'Every fix teaches',
    line: 'Corrections show the job what’s right.',
    description:
      'When you correct a value, Cortexa keeps it as the right answer for this job, and uses what it learns only once it’s proven right.',
  },
  {
    id: 'windows',
    host: 'detached-panels',
    kind: 'feature',
    art: 'windows',
    headline: 'Spread out across monitors',
    line: 'Put the page and the fields on different screens.',
    description:
      'Put the page on one monitor and the fields on another. Each window remembers where you left it.',
  },
  {
    id: 'barcode-starts',
    kind: 'use',
    art: 'barcode',
    headline: 'Every document starts with a barcode?',
    line: 'Start documents at your barcodes.',
    description:
      'If each of your documents starts with a barcode, tell the job to start documents at barcodes, and its splits follow them.',
  },
  {
    id: 'smarter',
    kind: 'feature',
    art: 'aiq',
    headline: 'Smarter every day',
    line: 'Every confirm makes this job smarter.',
    description:
      'Each document you confirm or correct is something the job learns from. The more you use it, the more it gets right on its own.',
  },
  {
    id: 'focus',
    kind: 'use',
    art: 'focus',
    headline: 'See each value beside its source',
    line: 'Focus puts the page bit next to each field.',
    description:
      'Focus shows each value next to the bit of the page it came from, so you can check a whole document at a glance.',
  },
  {
    id: 'sheets-with-values',
    kind: 'feature',
    art: 'separator',
    headline: 'Sheets that fill in fields',
    line: 'Break sheets can carry values too.',
    description:
      'Break sheets can also carry values, like an account number, that fill in the document that follows.',
  },
  {
    id: 'ctrl-enter',
    kind: 'use',
    art: 'keys',
    headline: 'Confirm in one keystroke',
    line: 'Ctrl+Enter confirms and moves on.',
    description:
      'In review, Ctrl+Enter confirms the whole document and opens the next one, from anywhere on the screen. Press F1 any time to see every shortcut.',
  },
  {
    id: 'autopilot',
    kind: 'feature',
    art: 'autopilot',
    headline: 'Let it run',
    line: 'Turn on Autopilot once it has learned enough.',
    description:
      'When your job has learned enough from you, switch it to Autopilot and it exports every document on its own, without you needing to get involved.',
  },
  {
    id: 'search',
    kind: 'use',
    art: 'search',
    headline: 'Find any document',
    line: 'Search a name, number or phrase.',
    description: 'Search finds a name, number or phrase across every document in the job.',
  },
  {
    id: 'page-count',
    kind: 'feature',
    art: 'plain',
    headline: 'Page 1 of 3? It notices.',
    line: 'Printed page counts keep documents together.',
    description:
      'Paige reads printed page counts like “Page 1 of 3” to keep each document’s pages together.',
  },
  {
    id: 'split',
    kind: 'use',
    art: 'separator',
    headline: 'Wrong split? Fix it in a click',
    line: 'Ctrl+B starts a new document at a page.',
    description:
      'If documents were split in the wrong place, fix it in a click, or press Ctrl+B to start a new document at a page. Ctrl+Z undoes it.',
  },
  {
    id: 'clean-pages',
    kind: 'feature',
    art: 'plain',
    headline: 'Crooked or blank pages? Handled.',
    line: 'It straightens pages and removes blanks.',
    description: 'Crooked pages are straightened and blank backs removed before anything is read.',
  },
  {
    id: 'not-sure',
    kind: 'use',
    art: 'review',
    headline: 'It knows when it isn’t sure',
    line: 'You can have it wait for you when it’s unsure.',
    description:
      'When Cortexa isn’t sure of a value, you can have the document wait for you instead of going out with a guess, and it tells you why.',
  },
  {
    id: 'values-travel',
    host: 'desktop-export',
    kind: 'feature',
    art: 'export',
    headline: 'Values travel with the file',
    line: 'Into SharePoint and Google Drive too.',
    description:
      'Sending to SharePoint or Google Drive? The values can travel with each file, so they’re there to search and sort.',
  },
  {
    id: 'undo',
    kind: 'use',
    art: 'undo',
    headline: 'Changed your mind? Undo it',
    line: 'Almost every step can be taken back.',
    description:
      'Almost everything can be undone, from a split to a suggested change, so it’s safe to try.',
  },
  {
    id: 'schedule',
    host: 'desktop-export',
    kind: 'feature',
    art: 'autopilot',
    headline: 'Export on your schedule',
    line: 'As soon as ready, on a schedule, or when you say.',
    description:
      'Choose when documents go out: as soon as they’re ready, on a schedule at a time you pick each day, or only when you say.',
  },
  {
    id: 'enter',
    kind: 'use',
    art: 'keys',
    headline: 'Review without the mouse',
    line: 'Enter moves on; Ctrl+Enter confirms.',
    description:
      'In review, Enter saves what you typed and moves to the next field, and Ctrl+Enter confirms the whole document and opens the next one.',
  },
  {
    id: 'formats',
    host: 'desktop-export',
    kind: 'feature',
    art: 'export',
    headline: 'Searchable PDFs and data files',
    line: 'Ready for whatever system is next.',
    description: 'Each document can go out as a searchable PDF, plus a data file with its values.',
  },
  {
    id: 'f1',
    kind: 'use',
    art: 'keys',
    headline: 'Every shortcut, one key away',
    line: 'Press F1 to see them all.',
    description: 'Press F1 any time to see every keyboard shortcut.',
  },
  {
    id: 'reorder',
    kind: 'use',
    art: 'plain',
    headline: 'Put fields in your order',
    line: 'Check them the way you like.',
    // PAIGE: the order is kept in this browser (field-display-order.tsx), not with the job.
    description: 'Put fields in the order you like to check them. This browser remembers it.',
  },
  {
    id: 'backup',
    host: 'desktop-export',
    kind: 'feature',
    art: 'carry',
    headline: 'A safety copy, kept for you',
    line: 'Keep a backup of every document.',
    description:
      'Turn on a backup copy and every document is also kept, for as long as you choose.',
  },
  {
    id: 'teach',
    kind: 'use',
    art: 'learn',
    headline: 'Want it to learn faster?',
    line: 'Teach offers good examples. Optional.',
    description:
      'Teach shows good examples you can confirm to help the job learn faster. It’s never required.',
  },
  {
    id: 'barcode-fields',
    kind: 'feature',
    art: 'barcode',
    headline: 'Read barcodes into fields',
    line: 'Any field can come from a barcode.',
    description: 'Any field can be read from a barcode, from Code 39 to QR codes.',
  },
  {
    id: 'computer-check',
    host: 'local-performance',
    kind: 'feature',
    art: 'gauge',
    headline: 'What can this computer do?',
    line: 'Computer check shows what it can do.',
    description:
      'Computer check shows whether Paige is ready on this computer and whether Speed boost is on.',
  },
  {
    id: 'history',
    kind: 'use',
    art: 'learn',
    headline: 'See what you’ve taught it',
    line: 'Every correction, in one place.',
    description: 'You can always see the corrections you’ve made, and what you’ve taught the job.',
  },
  {
    id: 'aiq',
    kind: 'use',
    art: 'aiq',
    headline: 'Watch it get smarter',
    line: 'The brain in the corner is your job’s AIQ.',
    description:
      'AIQ is your job’s learning score. You teach it and it gets smarter. Click it any time to see what it has learned.',
  },
  {
    id: 'ai-review',
    kind: 'feature',
    art: 'autopilot',
    needs: 'ai-review',
    headline: 'An expert second look, built in',
    line: 'When it’s unsure, it calls in a smarter AI.',
    description:
      'On Autopilot, when Cortexa is unsure, it checks the document against the page, then sends it on its own; what it can’t settle goes out flagged with the reason.',
  },
  {
    id: 'pause',
    host: 'job-pause',
    kind: 'use',
    art: 'pause',
    headline: 'Take a break anytime',
    line: 'Pause, then pick up where you left off.',
    description:
      'Pause stops new work from starting. What’s in progress finishes and is saved, and Resume picks up where you left off.',
  },
  {
    id: 'carry',
    host: 'local-storage',
    kind: 'feature',
    art: 'carry',
    headline: 'Take your job anywhere',
    line: 'Use what it learned on another computer.',
    description:
      'Download a job’s setup, with everything it learned, to use it on another computer. No scanned documents go with it.',
  },
  {
    id: 'legacy',
    host: 'legacy-import',
    kind: 'feature',
    art: 'carry',
    headline: 'Coming from earlier Paige?',
    line: 'Bring your old jobs with you.',
    description: 'Bring in your jobs from earlier Paige and keep going from there.',
  },
  {
    id: 'appearance',
    host: 'interface-zoom',
    kind: 'feature',
    art: 'plain',
    headline: 'Make it yours',
    line: 'Light or dark, your accent color, your size.',
    description: 'Choose light or dark, your accent color and the size of text.',
  },
  {
    id: 'early',
    host: 'software-updates',
    kind: 'feature',
    art: 'aiq',
    headline: 'Try new features first',
    line: 'Turn it on in Settings.',
    description: 'Turn on new features before everyone else gets them.',
  },
];

/** How long one tip shows before the next, long enough to read it and press its button. */
export const TIP_MILLISECONDS = 12_000;
/**
 * Opened, the tips keep going too, more slowly, so someone can stay in the opened view and
 * read them all as setup runs (owner, October 3, 2026); back and next step through them by
 * hand, and the pointer or keyboard on the panel holds the one being read.
 */
export const OPEN_TIP_MILLISECONDS = 24_000;

// PAIGE: a tip the host says in its own words (host-words.ts) shows in them, even where its `host` feature is hidden.
export const tipsFor = (computer: { aiReview: boolean }) =>
  SETUP_TIPS.filter(
    (tip) =>
      (!tip.needs || (tip.needs === 'ai-review' && computer.aiReview)) &&
      (!tip.host || hostHas(tip.host) || Boolean(hostWords().tips?.[tip.id])),
  ).map((tip) => ({ ...tip, ...hostWords().tips?.[tip.id] }));

export type TipClock = { index: number; shownAt: number; openId: string | null; led?: boolean };

/**
 * One rotation per job, kept for the whole setup: the setup screen and the wizard that
 * waits over the job afterwards are separate mounts, and an opened tip (or the next tip
 * in line) must not start over between them.
 */
const clocks = new Map<string, TipClock>();
export const tipClock = (jobId: string, now: number): TipClock => {
  const known = clocks.get(jobId);
  if (known) return known;
  const fresh = { index: 0, shownAt: now, openId: null };
  clocks.set(jobId, fresh);
  return fresh;
};
export const saveTipClock = (jobId: string, clock: TipClock) => {
  clocks.set(jobId, clock);
};

/**
 * The next state of the rotation, over the tips shown on this computer (their ids, in
 * order). It never moves while the pointer or keyboard is on the tips, and after that each
 * tip keeps at least a couple of seconds more. Closed, a tip shows for TIP_MILLISECONDS;
 * opened, the next tip opens after OPEN_TIP_MILLISECONDS, so the opened view reads on.
 */
export function advanceTipClock(
  clock: TipClock,
  now: number,
  ids: readonly string[],
  held: boolean,
): TipClock {
  if (ids.length <= 0) return clock;
  const period = clock.openId ? OPEN_TIP_MILLISECONDS : TIP_MILLISECONDS;
  if (held) {
    const shownAt = Math.max(clock.shownAt, now - period + 2_000);
    return shownAt === clock.shownAt ? clock : { ...clock, shownAt };
  }
  if (now - clock.shownAt < period) return clock;
  return stepTipClock(clock, now, ids, 1);
}

/** Back or next by hand (and the automatic next), keeping the tip opened when it is. */
export function stepTipClock(
  clock: TipClock,
  now: number,
  ids: readonly string[],
  by: 1 | -1,
): TipClock {
  if (ids.length <= 0) return clock;
  const index = (clock.index + by + ids.length) % ids.length;
  return { ...clock, index, shownAt: now, openId: clock.openId ? ids[index] : null };
}

/**
 * When learning ends, the tips go to one that says the job keeps learning (owner, October 3,
 * 2026: nothing on the setup screen may suggest learning stops with setup). Once per job, and
 * never over a tip someone has opened: that one stays as it is.
 */
export const LEARNING_DONE_TIP = 'smarter';
export function leadTipClock(
  clock: TipClock,
  now: number,
  ids: readonly string[],
  leadId: string,
): TipClock {
  if (clock.led) return clock;
  const index = ids.indexOf(leadId);
  if (clock.openId || index < 0) return { ...clock, led: true };
  return { ...clock, index, shownAt: now, led: true };
}
